import { useCallback, useEffect, useRef } from 'react';
import { type BlockerFunction, useBlocker } from 'react-router-dom';

/**
 * Hook to intercept navigation and prompt the user when there are unsaved changes
 */
export function useNavigationGuard(
  hasUnsavedChanges: boolean,
  onDiscardChanges?: () => void
) {
  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => {
      if (nextLocation.state?.skipGuard) return false;
      if (!hasUnsavedChanges) return false;
      if (nextLocation.pathname === currentLocation.pathname) return false;

      const normalize = (pathname: string) => pathname.replace(/\/edit$/, '');

      return (
        normalize(currentLocation.pathname) !==
          normalize(nextLocation.pathname) ||
        currentLocation.pathname.endsWith('/edit')
      );
    },
    [hasUnsavedChanges]
  );

  const blocker = useBlocker(shouldBlock);
  const isConfirmingRef = useRef(false);

  useEffect(() => {
    if (blocker.state === 'blocked' && !isConfirmingRef.current) {
      isConfirmingRef.current = true;

      const confirmLeave = window.confirm(
        'You have unsaved changes that will be lost. Do you want to leave this page?'
      );

      if (confirmLeave) {
        onDiscardChanges?.();
        blocker.proceed();
      } else {
        blocker.reset();
        isConfirmingRef.current = false;
      }
    }

    // Only reset the confirming flag when truly idle — NOT during 'proceeding'.
    // In React Router v7, proceed() transitions through blocked → proceeding → idle.
    // Resetting during 'proceeding' allows shouldBlock to re-evaluate and re-trigger
    // the confirm dialog in an infinite loop before navigation completes.
    if (blocker.state === 'unblocked') {
      isConfirmingRef.current = false;
    }
  }, [blocker, onDiscardChanges]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges) return;

      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);
}
